import { useAuth } from '@platform/auth-client';
import { Button, ConfirmDialog, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { LoadFailed } from '../LoadFailed.tsx';
import rows from '../settings/Rows.module.css';
import { SETTINGS_SECTIONS } from '../settings/settings-sections.ts';
import { SettingsSection } from '../settings/SettingsSection.tsx';
import { useSubsidiaries } from '../settings/subsidiaries.ts';
import { usePageContext } from '../usePageContext.ts';
import { InviteSheet } from './InviteSheet.tsx';
import { MemberSheet, type MemberCommand } from './MemberSheet.tsx';
import { MemberBadges } from './MemberBadges.tsx';
import { invitationMessage } from './invitation-message.ts';
import {
  ADMIN_ROLE,
  displayNameOf,
  statusOf,
  useTeam,
  useTeamActions,
  USERS_MANAGE_ROLE,
  type Member,
  type TeamAction,
} from './team.ts';

type Sheet = { readonly kind: 'invite' } | { readonly kind: 'member'; readonly member: Member };
type Confirm = { readonly command: 'pause' | 'remove'; readonly member: Member };

export const TeamSection = ({ api }: { api: TypedClient }) => {
  const auth = useAuth();
  if (!auth.hasRole(USERS_MANAGE_ROLE)) return null;
  return <TeamDirectory api={api} selfId={auth.principal?.userId ?? ''} isAdmin={auth.hasRole(ADMIN_ROLE)} />;
};

const TeamDirectory = ({ api, selfId, isAdmin }: { api: TypedClient; selfId: string; isAdmin: boolean }) => {
  const { t, notify, describeError } = usePageContext();
  const team = useTeam(api);
  const subsidiaries = useSubsidiaries(api).data ?? [];
  const actions = useTeamActions(api);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [confirming, setConfirming] = useState<Confirm | null>(null);
  const close = () => {
    setSheet(null);
  };
  const errorMessage = actions.isError ? (describeError(actions.error) ?? null) : null;

  const perform = (action: TeamAction, message: string) => {
    actions.mutate(action, {
      onSuccess: () => {
        notify(message);
        close();
      },
    });
  };

  const run = (member: Member, command: MemberCommand) => {
    if (command === 'pause' || command === 'remove') setConfirming({ command, member });
    if (command === 'resume')
      perform({ kind: 'status', member, active: true }, t('team.resumed', { name: displayNameOf(member) }));
    if (command === 'resend') perform({ kind: 'resend', member }, t('team.resent', { email: member.email }));
  };

  const confirm = ({ command, member }: Confirm) => {
    setConfirming(null);
    const name = displayNameOf(member);
    if (command === 'pause') perform({ kind: 'status', member, active: false }, t('team.paused', { name }));
    else perform({ kind: 'remove', member }, t('team.removed', { name }));
  };

  const rowOf = (member: Member) => {
    const self = member.userId === selfId;
    const content = (
      <>
        <span className={rows['mark']} aria-hidden="true">
          {displayNameOf(member).charAt(0).toUpperCase()}
        </span>
        <span className={rows['text']}>
          <span className={rows['title']}>
            {displayNameOf(member)}
            <MemberBadges member={member} self={self} subsidiaries={subsidiaries} />
          </span>
          <span className={rows['meta']} dir="ltr">
            {member.email}
          </span>
          {self ? <span className={rows['meta']}>{t('team.selfNote')}</span> : null}
        </span>
      </>
    );
    return (
      <li key={member.userId}>
        {self ? (
          <div className={rows['row']}>{content}</div>
        ) : (
          <button
            type="button"
            className={[rows['row'], rows['rowButton']].join(' ')}
            data-status={statusOf(member)}
            onClick={() => {
              actions.reset();
              setSheet({ kind: 'member', member });
            }}
          >
            {content}
          </button>
        )}
      </li>
    );
  };

  const body = () => {
    if (team.isPending) return <Skeleton height="64px" label={t('team.title')} />;
    if (team.isError)
      return (
        <LoadFailed
          description={describeError(team.error)}
          onRetry={() => {
            void team.refetch();
          }}
        />
      );
    return <ul className={rows['rows']}>{team.data.map(rowOf)}</ul>;
  };

  return (
    <SettingsSection
      id={SETTINGS_SECTIONS.team}
      icon={Users}
      title={t('team.title')}
      description={t('team.context')}
      action={
        <Button
          variant="outline"
          icon={<UserPlus size={20} aria-hidden="true" />}
          onClick={() => {
            actions.reset();
            setSheet({ kind: 'invite' });
          }}
        >
          {t('team.invite')}
        </Button>
      }
    >
      {body()}
      {sheet?.kind === 'invite' ? (
        <InviteSheet
          subsidiaries={subsidiaries}
          isAdmin={isAdmin}
          submitting={actions.isPending}
          errorMessage={errorMessage}
          onClose={close}
          onSubmit={(body) => {
            actions.mutate(
              { kind: 'invite', body },
              {
                onSuccess: (outcome) => {
                  notify(invitationMessage(t, body, outcome));
                  close();
                },
              },
            );
          }}
        />
      ) : null}
      {sheet?.kind === 'member' ? (
        <MemberSheet
          member={sheet.member}
          subsidiaries={subsidiaries}
          isAdmin={isAdmin}
          submitting={actions.isPending}
          errorMessage={errorMessage}
          onClose={close}
          onSubmit={(body) => {
            perform(
              { kind: 'access', member: sheet.member, body },
              t('team.saved', { name: displayNameOf(sheet.member) }),
            );
          }}
          onCommand={(command) => {
            run(sheet.member, command);
          }}
        />
      ) : null}
      {confirming === null ? null : (
        <ConfirmDialog
          title={t(`team.${confirming.command}Title`, { name: displayNameOf(confirming.member) })}
          message={t(`team.${confirming.command}Message`)}
          confirmLabel={t(`team.${confirming.command}`)}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            confirm(confirming);
          }}
        />
      )}
    </SettingsSection>
  );
};
